import { Skeleton } from '@/common/ui/skeleton'
import { Card, CardContent } from '@/common/ui/card'
import {
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from '@/common/ui/table'
import { useQueryGetChannelCohortAnalytics } from '@/modules/dashboard/api/hooks/useQueryGetChannelCohortAnalytics'
import type { ChannelCohortPeriodAnalytics } from '@/modules/dashboard/type'

interface Props {
	channelId: string
}

const fmt = (n: number) =>
	new Intl.NumberFormat('th-TH', {
		minimumFractionDigits: 0,
		maximumFractionDigits: 2,
	}).format(n)

function dayLabel(day: number): string {
	if (day === -1) return 'สะสม'
	if (day === 1) return '1 วัน'
	return `${day} วัน`
}

type Column = {
	header: string
	value: (a: ChannelCohortPeriodAnalytics) => React.ReactNode
}

const COLUMNS: Column[] = [
	{
		header: 'ผู้เล่นทั้งหมด',
		value: (a) => fmt(a.totalPlayerCount),
	},
	{
		header: 'ผู้เล่นที่ Active',
		value: (a) => fmt(a.totalActivePlayerCount),
	},
	{
		header: 'ฝากรวม',
		value: (a) => fmt(a.totalDepositAmount),
	},
	{
		header: 'ถอนรวม',
		value: (a) => fmt(a.totalWithdrawAmount),
	},
	{
		header: 'กำไร',
		value: (a) => (
			<span
				className={
					a.totalProfit >= 0
						? 'text-green-600 dark:text-green-400'
						: 'text-red-500 dark:text-red-400'
				}
			>
				{fmt(a.totalProfit)}
			</span>
		),
	},
	{
		header: 'จำนวนครั้งฝาก/ถอน',
		value: (a) => `${fmt(a.totalDepositTime)} / ${fmt(a.totalWithdrawTime)}`,
	},
	{
		header: 'เฉลี่ยฝาก/ถอนต่อคน',
		value: (a) =>
			`${fmt(a.totalDepositAmountAvg)} / ${fmt(a.totalWithdrawAmountAvg)}`,
	},
	{
		header: 'เฉลี่ยครั้งฝาก/ถอนต่อคน',
		value: (a) =>
			`${fmt(a.totalDepositTimeAvg)} / ${fmt(a.totalWithdrawTimeAvg)}`,
	},
	{
		header: 'เฉลี่ยกำไรต่อคน',
		value: (a) => (
			<span
				className={
					a.totalProfitAvg >= 0
						? 'text-green-600 dark:text-green-400'
						: 'text-red-500 dark:text-red-400'
				}
			>
				{fmt(a.totalProfitAvg)}
			</span>
		),
	},
	{
		header: 'ต้นทุนต่อคน',
		value: (a) => (a.costPerPlayer != null ? fmt(a.costPerPlayer) : '-'),
	},
	{
		header: 'ต้นทุนต่อคน Active',
		value: (a) =>
			a.costPerActivePlayer != null ? fmt(a.costPerActivePlayer) : '-',
	},
	{
		header: 'กำไร - ต้นทุน',
		value: (a) => {
			if (a.costMinusProfit == null) return '-'
			return (
				<span
					className={
						a.costMinusProfit >= 0
							? 'text-green-600 dark:text-green-400'
							: 'text-red-500 dark:text-red-400'
					}
				>
					{fmt(a.costMinusProfit)}
				</span>
			)
		},
	},
]

export function ChannelCohortTable({ channelId }: Props) {
	const { data, isLoading } = useQueryGetChannelCohortAnalytics(channelId)

	if (isLoading) {
		return (
			<Card>
				<CardContent className="p-6">
					<div className="space-y-2">
						{Array.from({ length: 5 }).map((_, i) => (
							<Skeleton key={i} className="h-8 w-full" />
						))}
					</div>
				</CardContent>
			</Card>
		)
	}

	if (!data) return null

	const rows = data.analytics

	return (
		<div className="w-full overflow-x-auto rounded-md border">
			<Table>
				<TableHeader>
					<TableRow>
						<TableHead className="sticky left-0 z-10 min-w-[90px] bg-background font-semibold after:absolute after:right-0 after:top-0 after:h-full after:w-px after:bg-border after:content-['']">
							ช่วงเวลา
						</TableHead>
						{COLUMNS.map((col) => (
							<TableHead
								key={col.header}
								className="min-w-[140px] whitespace-nowrap text-right"
							>
								{col.header}
							</TableHead>
						))}
					</TableRow>
				</TableHeader>
				<TableBody>
					{rows.map((row) => (
						<TableRow
							key={row.dayAfterActivation}
							className={
								row.dayAfterActivation === -1
									? 'bg-muted/50 font-semibold'
									: undefined
							}
						>
							<TableCell className="sticky left-0 z-10 bg-background font-medium after:absolute after:right-0 after:top-0 after:h-full after:w-px after:bg-border after:content-['']">
								{dayLabel(row.dayAfterActivation)}
							</TableCell>
							{COLUMNS.map((col) => (
								<TableCell
									key={col.header}
									className="whitespace-nowrap text-right tabular-nums"
								>
									{col.value(row)}
								</TableCell>
							))}
						</TableRow>
					))}
				</TableBody>
			</Table>
		</div>
	)
}
